import type { AttachmentState, AttachmentMenuAction } from 'owa-attachment-full-data';
import type { REFRESH_SHARING_TIPS_ATTACHMENT_WELLActionMessage } from './actions/publicActions';
import type { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import type { SharingTipRecipientInfo } from 'owa-sharing-data';
import type { ItemId } from 'owa-graph-schema';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type { MailboxInfo } from 'owa-client-types';
import type { ClientItemId } from 'owa-client-ids';
import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';
import type AttachmentWellViewState from './schema/AttachmentWellViewState';

export { default as onAttachmentCreated } from './actions/onAttachmentCreated';
export { default as onAttachmentDeleted } from './actions/onAttachmentDeleted';
export type { default as AttachmentWellViewState } from './schema/AttachmentWellViewState';
/* eslint-disable-next-line owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Baseline comment; do not copy-paste. Rexporting from a separate package complicates the package graph.
 *	> Do not re-export from a separate package */
export { default as ImageLoadState } from 'owa-attachment-data/lib/schema/ImageLoadState';
/* eslint-disable-next-line owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Baseline comment; do not copy-paste. Rexporting from a separate package complicates the package graph.
 *	> Do not re-export from a separate package */
export type { default as AttachmentFullViewState } from 'owa-attachment-full-data/lib/schema/AttachmentFullViewState';
/* eslint-disable-next-line owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Baseline comment; do not copy-paste. Rexporting from a separate package complicates the package graph.
 *	> Do not re-export from a separate package */
export type { default as AttachmentState } from 'owa-attachment-full-data/lib/schema/AttachmentState';
/* eslint-disable-next-line owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Baseline comment; do not copy-paste. Rexporting from a separate package complicates the package graph.
 *	> Do not re-export from a separate package */
export { default as AttachmentStateType } from 'owa-attachment-full-data/lib/schema/AttachmentStateType';
/* eslint-disable-next-line owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Baseline comment; do not copy-paste. Rexporting from a separate package complicates the package graph.
 *	> Do not re-export from a separate package */
export { default as InlineAttachmentStatus } from 'owa-attachment-full-data/lib/schema/InlineAttachmentStatus';

export type {
    CreateAttachmentsProperties,
    ShareAttachmentProperties,
    ShouldShareAttachment,
} from './types/CreateAttachmentsProperties';

export type {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from './types/AttachmentUploadQueueItem';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "AttachmentsData"*/ './lazyIndex'),
    { name: 'AttachmentsData' }
);

// Delayed Loaded Actions
export const lazyInitializeAttachments: LazyAction<
    (
        attachments: AttachmentType[],
        mailboxInfo: MailboxInfo,
        attachmentWell: AttachmentWellViewState | undefined,
        forceUpdateAttachmentsInStore: boolean,
        _isInitializationForCompose: boolean,
        unsupportedMenuActions?: AttachmentMenuAction[] | undefined,
        parentItemId?: ClientItemId | undefined,
        isMessageEncrypted?: boolean | undefined,
        isSMIMEMessage?: boolean | undefined,
        isProtectedVoiceMail?: boolean | undefined,
        shouldBlockAttachment?: boolean | undefined,
        isCalendarItem?: boolean | undefined
    ) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.initializeAttachments);

export const lazyReinitializeAttachments: LazyAction<
    typeof lazyIndex.reinitializeAttachments,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.reinitializeAttachments);

export const lazyAddAttachmentIntoWellBeforeUpload: LazyAction<
    typeof lazyIndex.addAttachmentIntoWellBeforeUpload,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.addAttachmentIntoWellBeforeUpload);

export const lazyRefreshSharingTipsAttachmentWell: LazyImport<
    (
        attachmentWellViewState: AttachmentWellViewState,
        parentItemId: ItemId,
        recipientInfo: SharingTipRecipientInfo[],
        infoBarIds: string[],
        infoBar: InfoBarHostViewState,
        mailboxInfo: MailboxInfo
    ) => REFRESH_SHARING_TIPS_ATTACHMENT_WELLActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.refreshSharingTipsAttachmentWell);

// Delay Loaded utils
export const lazyCreateAttachmentsViaQueueManager: LazyImport<
    typeof lazyIndex.createAttachmentsViaQueueManager,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.createAttachmentsViaQueueManager);

export const lazycreateAttachmentsFromRecentAttachments: LazyImport<
    typeof lazyIndex.createAttachmentsFromRecentAttachments,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.createAttachmentsFromRecentAttachments);

export const lazyDeleteAttachmentsViaQueueManager: LazyAction<
    typeof lazyIndex.deleteAttachmentViaQueueManager,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.deleteAttachmentViaQueueManager);

export const lazyCreateAttachmentFullViewStateAndStoreBackingModel: LazyImport<
    typeof lazyIndex.createAttachmentFullViewStateAndStoreBackingModel,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.createAttachmentFullViewStateAndStoreBackingModel);

export const lazyCreateAttachmentViewState: LazyImport<
    typeof lazyIndex.createAttachmentFullViewState,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.createAttachmentFullViewState);

export const lazyGetAttachmentWellInitialValue: LazyImport<
    typeof lazyIndex.getAttachmentWellInitialValue,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getAttachmentWellInitialValue);

export const lazyGetAttachmentWellForCompose: LazyImport<
    typeof lazyIndex.getAttachmentWellForCompose,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getAttachmentWellForCompose);

export const lazyGetAvailableLocalAttachmentSize: LazyImport<
    typeof lazyIndex.getAvailableLocalAttachmentSize,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getAvailableLocalAttachmentSize);

export const lazyConvertImageViewAttachmentToInlineAttachment: LazyAction<
    typeof lazyIndex.convertImageViewAttachmentToInlineAttachment,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.convertImageViewAttachmentToInlineAttachment);

export const lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend: LazyImport<
    typeof lazyIndex.getLastCloudyAttachmentsSharingIssueForBlockOnSend,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getLastCloudyAttachmentsSharingIssueForBlockOnSend);

export const lazyGetAllValidAttachments: LazyImport<
    (
        attachmentWell: AttachmentWellViewState,
        content: string,
        bodyType: string,
        filterSmimeAttachment: boolean
    ) => AttachmentState[],
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getAllValidAttachments);

export const lazyUploadLocalFile: LazyImport<
    typeof lazyIndex.uploadLocalFile,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.uploadLocalFile);

export const lazyGetUpsellProperties: LazyImport<
    typeof lazyIndex.getUpsellProperties,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.getUpsellProperties);

export const lazyUpdateOfflineAttachmentWithServerAttachment: LazyImport<
    typeof lazyIndex.updateOfflineAttachmentWithServerAttachment,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.updateOfflineAttachmentWithServerAttachment);

// boot strap - used to load the package to make mutators work
export { lazyModule as preloadAttachmentsData };
